import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { openCertificate } from '@/lib/certificates';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Loader2, CalendarDays, Clock, MapPin, Video, Users, Star, RotateCcw,
  Phone, Info, User, Ban, Archive, BellRing,
} from 'lucide-react';
import { isOfflineMode, formatDay, formatTime, venueText } from './AssignMentorDialog';
import { SC, STATUS_LABEL, experienceActions, StatusBadge } from './ExperienceTable';
import { lifecycleState, formatDayInSchoolTz } from '@/lib/experienceStatus';
import { SessionAgenda, showAgendaFor } from '@/components/SessionAgenda';

const WAITLIST_REASONS = {
  no_mentor: 'No approved mentor covers this career yet',
  no_online_availability: 'No mentor has online availability set up',
  no_offline_visit: 'No mentor has published a workplace visit',
  capacity_full: 'Every suitable session is full',
};

import { CancelExperienceDialog } from './CloseExperienceDialog';
import { AdminCloseDialog } from './AdminCloseDialog';
import FeedbackReminderButton from './FeedbackReminderButton';
import { toast } from 'sonner';

const CONSENT_LABEL = { granted: 'Consent active', withdrawn: 'Consent withdrawn', pending: 'Consent pending' };
const CONSENT_TONE = {
  granted: 'bg-emerald-50 text-emerald-600',
  withdrawn: 'bg-red-50 text-red-600',
  pending: 'bg-amber-50 text-amber-700',
};

/** Nudges the mentor to confirm a past experience. Once a day, server enforced. */
function MentorReminderButton({ booking, onSent }) {
  const [sending, setSending] = useState(false);
  const send = async () => {
    setSending(true);
    try {
      await api.post(`/coordinator/bookings/${booking.id}/completion-reminder`);
      toast.success('Reminder sent to the mentor');
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send the reminder');
    }
    setSending(false);
  };
  return (
    <Button size="sm" variant="outline" className="rounded-lg text-xs" disabled={sending} onClick={send} data-testid="details-remind-mentor-btn">
      {sending ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <BellRing className="h-3 w-3 mr-1" />}
      Remind mentor
    </Button>
  );
}

/**
 * The single experience details panel used everywhere in the coordinator
 * dashboard — Overview, the tables, Follow-ups and the school calendar.
 * It never navigates away, so coordinator auth and school scope are kept.
 */
export default function ExperienceDetailsDialog({ booking, bookings = [], onClose, onAssign, onReschedule, onRefresh }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminClosing, setAdminClosing] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/bookings/${booking.id}/detail`)
      .then(r => { if (active) setDetail(r.data); })
      .catch(() => { if (active) setDetail(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [booking.id]);

  const load = () => api.get(`/bookings/${booking.id}/detail`).then(r => setDetail(r.data)).catch(() => {});
  const refreshAll = () => { load(); onRefresh?.(); };

  // Consent standing for each student in a grouped session, so it reads here and not on the profile
  const [consentByBooking, setConsentByBooking] = useState({});
  useEffect(() => {
    if (!booking.session_id) return undefined;
    let active = true;
    api.get(`/sessions/${booking.session_id}`)
      .then(r => {
        if (!active) return;
        const map = {};
        (r.data.students || []).forEach(st => { map[st.booking_id] = st.consent_status; });
        setConsentByBooking(map);
      })
      .catch(() => {});
    return () => { active = false; };
  }, [booking.session_id]);

  const b = { ...booking, ...(detail || {}) };
  const offline = isOfflineMode(b.assigned_mode || b.requested_mode);
  const venue = b.assigned_venue || {};
  const participants = b.session_id
    ? bookings.filter(x => x.session_id === b.session_id && !['cancelled', 'rejected', 'not_conducted'].includes(x.status))
    : [b];
  const actions = experienceActions(b);
  const closed = () => { onRefresh?.(); onClose(); };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-lg max-h-[88vh] overflow-y-auto" data-testid="experience-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2 flex-wrap">
            {b.career_name}
            <StatusBadge booking={b} testId="details-status" />
          </DialogTitle>
          <DialogDescription>{offline ? 'Workplace visit' : 'Online session'}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-[#9CA3AF]" /></div>
        ) : (
          <div className="space-y-3 text-xs">
            <Line icon={User} testid="details-student">
              <strong className="text-[#1F2937]">{b.child_name}</strong>
              {b.child_grade ? ` · Grade ${b.child_grade}` : ''}{b.child_division ? `-${b.child_division}` : ''}
              {b.parent_name ? ` · ${b.parent_name}` : ''}
            </Line>
            <Line icon={Users} testid="details-mentor">
              {b.mentor_name || (b.suggested_mentor_name ? `Suggested: ${b.suggested_mentor_name}` : 'Mentor unassigned')}
              {b.mentor_profession_title ? ` · ${b.mentor_profession_title}` : ''}
              {b.mentor_company_name ? `, ${b.mentor_company_name}` : ''}
            </Line>
            <Line icon={CalendarDays} testid="details-date">
              {b.assigned_date ? formatDay(b.assigned_date) : 'Not scheduled yet'}
            </Line>
            <Line icon={Clock} testid="details-time">
              {b.assigned_time_slot ? formatTime(b.assigned_time_slot) : '—'}
              {b.assigned_end_time ? ` – ${formatTime(b.assigned_end_time)}` : ''}
              {b.assigned_duration_minutes ? ` (${b.assigned_duration_minutes} min)` : ''}
            </Line>

            {offline ? (
              <div className="bg-[#F8F9FA] rounded-xl p-3 space-y-2" data-testid="details-venue">
                <Line icon={MapPin}>{venueText(venue) || 'Venue to confirm'}</Line>
                {(venue.contact_name || venue.contact_phone) && (
                  <Line icon={Phone}>{[venue.contact_name, venue.contact_phone].filter(Boolean).join(' · ')}</Line>
                )}
                {venue.visit_instructions && <Line icon={Info}>{venue.visit_instructions}</Line>}
                <p className="text-[11px] text-[#6B7280] leading-relaxed">
                  {venue.supervised_by === 'workplace_host'
                    ? `${venue.host_name || 'An approved workplace host'} supervises the students.`
                    : `${b.mentor_name || 'The approved mentor'} supervises the students.`}
                  {' '}Parents handle drop-off and pick-up unless the school arranges transport.
                </p>
              </div>
            ) : (
              <Line icon={Video} testid="details-meet">
                {b.google_meet_link
                  ? <a href={b.google_meet_link} target="_blank" rel="noreferrer" className="text-[#F06651] hover:underline break-all">{b.google_meet_link}</a>
                  : b.session_link
                    ? <a href={b.session_link} target="_blank" rel="noreferrer" className="text-[#F06651] hover:underline break-all">{b.session_link}</a>
                    : 'Meeting link pending'}
              </Line>
            )}

            {!offline && showAgendaFor(b.status) && <SessionAgenda compact />}

            {!offline && b.google_sync_status && !['synced', 'not_applicable'].includes(b.google_sync_status) && (              <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="details-sync-warning">
                The calendar invitation has not gone out yet. The assignment is saved — an admin can retry it from Integrations.
              </p>
            )}

            {participants.length > 1 && (
              <div className="bg-[#F8F9FA] rounded-xl p-3" data-testid="details-participants">
                <div className="flex items-center justify-between gap-2 mb-1.5">
                  <p className="text-[11px] font-semibold text-[#1F2937]">Grouped session · {participants.length} students</p>
                  <Link to={`/group-sessions/${b.session_id}`}
                        className="text-[11px] font-semibold text-[#6B7280] hover:text-[#1F2937] underline underline-offset-2 shrink-0"
                        data-testid="open-group-details">
                    Open group details
                  </Link>
                </div>
                {participants.map(p => (
                  <div key={p.id} className="flex items-center justify-between gap-2 py-0.5">
                    <span className="text-[11px] text-[#4B5563] truncate">{p.child_name}{p.child_grade ? ` · Grade ${p.child_grade}` : ''}</span>
                    <span className="flex items-center gap-1.5 shrink-0">
                      {consentByBooking[p.id] && (
                        <Badge className={`border-0 text-[9px] ${CONSENT_TONE[consentByBooking[p.id]]}`} data-testid={`details-consent-${p.id}`}>
                          {CONSENT_LABEL[consentByBooking[p.id]]}
                        </Badge>
                      )}
                      <Badge className={`border-0 text-[9px] ${SC[p.status] || 'bg-gray-100 text-gray-500'}`}>{STATUS_LABEL[p.status] || p.status}</Badge>
                    </span>
                  </div>
                ))}
              </div>
            )}

            {b.reschedule_request?.status === 'pending' && (
              <p className="text-[11px] text-violet-700 bg-violet-50 rounded-lg px-3 py-2" data-testid="details-reschedule-request">
                {b.reschedule_request.declined_slot
                  ? <>Parent declined {formatDay(b.reschedule_request.from_date)} and asked for another time. That slot is released.</>
                  : <>Parent asked for a different time. Nothing changes for the family until you approve one.</>}
                {b.reschedule_request.reason ? ` “${b.reschedule_request.reason}”` : ''}
                {b.reschedule_proposal
                  ? <> Time found: {formatDay(b.reschedule_proposal.assigned_date)} at {b.reschedule_proposal.assigned_time_slot} · {b.reschedule_proposal.mentor_name}.</>
                  : <> No suitable time was found yet, so it is on the waiting list.</>}
              </p>
            )}
            {b.status === 'cancelled' && b.cancelled_by && (
              <p className="text-[11px] text-red-600 bg-red-50 rounded-lg px-3 py-2" data-testid="details-cancelled">
                Cancelled by {b.cancelled_by}{b.cancellation_reason ? ` — “${b.cancellation_reason}”` : ''}
              </p>
            )}

            {b.status === 'not_conducted' && (
              <p className="text-[11px] text-slate-700 bg-slate-100 rounded-lg px-3 py-2" data-testid="details-not-conducted">
                Recorded as not conducted{b.closed_by_name ? ` by ${b.closed_by_name}` : ''}
                {b.not_conducted_reason ? ` — “${b.not_conducted_reason}”` : ''}. The reserved seats were released.
              </p>
            )}

            {/* What actually happened: completion, attendance, feedback and certificate
                are tracked separately, and so is any administrative closure. */}
            {['completed', 'awaiting_feedback'].includes(b.status) && (
              <div className="bg-[#F8F9FA] rounded-xl p-3 space-y-1.5" data-testid="details-completion">
                <p className="text-[11px] font-semibold text-[#1F2937]">Completion</p>
                {b.completed_at && <p className="text-[11px] text-[#6B7280]">Confirmed by the mentor on {formatDay(String(b.completed_at).slice(0, 10))}</p>}
                {b.attended !== undefined && b.attended !== null && (
                  <p className="text-[11px] text-[#6B7280]">Attendance: {b.attended ? 'Attended' : 'Did not attend'}</p>
                )}
                {b.feedback_rating ? (
                  <p className="text-[11px] text-[#4B5563] flex items-center gap-1" data-testid="details-rating">
                    {[1, 2, 3, 4, 5].map(n => (
                      <Star key={n} className={`h-3 w-3 ${n <= b.feedback_rating ? 'text-amber-400 fill-amber-400' : 'text-[#E5E7EB]'}`} />
                    ))}
                    <span className="ml-1">{b.feedback_rating}/5 from the family</span>
                  </p>
                ) : b.attended === false ? null : (
                  <p className="text-[11px] text-[#9CA3AF]" data-testid="details-feedback-pending">Family feedback not submitted yet</p>
                )}
                {b.feedback_text && <p className="text-[11px] text-[#4B5563] italic">“{b.feedback_text}”</p>}
                {b.mentor_student_feedback && (
                  <p className="text-[11px] text-[#4B5563]"><span className="text-[#9CA3AF]">Mentor note: </span>{b.mentor_student_feedback}</p>
                )}
                <p className="text-[11px] text-[#6B7280]" data-testid="details-certificate-status">
                  <span className="text-[#9CA3AF]">Certificate: </span>
                  {b.certificate_url ? 'issued' : b.attended === false ? 'not applicable — the student did not attend'
                    : 'issued once the family submits feedback'}
                </p>
                {b.certificate_url && (
                  <button onClick={() => openCertificate(b.certificate_url)} className="text-[11px] text-[#F06651] hover:underline" data-testid="details-certificate-link">Certificate</button>
                )}
              </div>
            )}

            {lifecycleState(b) === 'awaiting_completion' && b.status === 'confirmed' && (
              <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2" data-testid="details-awaiting-mentor">
                This date has passed and {b.mentor_name || 'the mentor'} has not confirmed what happened yet, so
                no attendance is recorded and no certificate can be issued.
                {b.completion_reminder_sent_at
                  ? ` Reminded on ${formatDayInSchoolTz(b.completion_reminder_sent_at)}.`
                  : ''}
              </p>
            )}

            {b.admin_closed_at && (
              <p className="text-[11px] text-slate-700 bg-slate-100 rounded-lg px-3 py-2" data-testid="details-admin-closed">
                Closed administratively by {b.admin_closed_by_name || 'a coordinator'} on {formatDayInSchoolTz(b.admin_closed_at)}
                {b.admin_closure_reason ? ` — “${b.admin_closure_reason}”` : ''}.
                {b.admin_closure_scope === 'feedback_outstanding'
                  ? ' Family feedback was never submitted, so no certificate was issued.'
                  : ' The mentor never confirmed attendance, so nothing was recorded as attended.'}
              </p>
            )}

            {b.parent_notes && <Detail label="Parent notes" value={b.parent_notes} />}
            {b.coordinator_notes && <Detail label="Coordinator notes" value={b.coordinator_notes} />}
            {b.waitlist_reason && <Detail label="Nothing matched because" value={WAITLIST_REASONS[b.waitlist_reason] || b.waitlist_reason} />}

            <div className="flex flex-wrap gap-2 pt-1">
              {actions.includes('assign') && (
                <Button size="sm" className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs" onClick={() => { onClose(); onAssign?.(booking); }} data-testid="details-assign-btn">
                  {offline ? 'Assign Slot' : 'Assign Mentor'}
                </Button>
              )}
              {actions.includes('reschedule') && (
                <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => { onClose(); onReschedule?.(booking); }} data-testid="details-reschedule-btn">
                  <RotateCcw className="h-3 w-3 mr-1" />Reschedule
                </Button>
              )}
              {actions.includes('cancel') && (
                <Button size="sm" variant="outline" className="rounded-lg text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700" onClick={() => setCancelling(true)} data-testid="details-cancel-btn">
                  <Ban className="h-3 w-3 mr-1" />Cancel
                </Button>
              )}
              {actions.includes('remind_mentor') && (
                <MentorReminderButton booking={b} onSent={refreshAll} />
              )}
              {actions.includes('remind') && (
                <FeedbackReminderButton booking={b} onSent={refreshAll} />
              )}
              {actions.includes('admin_close') && (
                <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => setAdminClosing(true)} data-testid="details-admin-close-btn">
                  <Archive className="h-3 w-3 mr-1" />Close administratively
                </Button>
              )}
              {actions.length === 0 && (
                <p className="text-[11px] text-[#9CA3AF]" data-testid="details-no-actions">
                  This experience is closed — details only.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      {adminClosing && (
        <AdminCloseDialog booking={b} onClose={() => setAdminClosing(false)} onDone={closed} />
      )}
      {cancelling && (
        <CancelExperienceDialog booking={b} onClose={() => setCancelling(false)} onDone={closed} />
      )}
    </Dialog>
  );
}

const Line = ({ icon: Icon, children, testid }) => (
  <div className="flex items-start gap-2 text-[#4B5563]" data-testid={testid}>
    <Icon className="h-3.5 w-3.5 text-[#9CA3AF] mt-0.5 shrink-0" />
    <span className="min-w-0">{children}</span>
  </div>
);

const Detail = ({ label, value }) => (
  <div><span className="text-[#9CA3AF] text-[11px]">{label}</span><p className="text-[#1F2937] whitespace-pre-wrap">{value}</p></div>
);
